// Combine this trip with the one immediately before or after it.
//
// RenameTripModal's sibling, and deliberately the same shape: a trip is not a
// row, so this writes every leg (or entry) of BOTH trips, which is nothing a
// per-keystroke autosave could express. `onCombine(name)` performs the write and
// throws; errors are shown inline here because the page's status bar is behind
// the modal.
//
// What the user is really deciding is the NAME, which is why the box is the body
// of the dialog rather than a detail of it. It is asked rather than assumed
// because neither answer is obviously right: joining a delivery leg to a cruise
// should take the cruise's name, and pulling a stray day into a trip usually
// wants a new one. The two chips fill the box with each trip's name; a trip
// with no name of its own offers none, for the reason Rename is seeded blank on
// an unnamed group -- `name` there is the app's own from-to title, and offering
// it back invites the user to save the app's wording as a trip name.
//
// `losses` are the things the merge will end that cannot be brought back: a
// share link revoked, a weather email cancelled. They are stated BEFORE the
// merge rather than reported after it, because "your public link stopped
// working" is not useful once it has.

import { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { extractErrorText } from '../../fleet-shared/Common.mjs';

// "Spring Shakedown (May 3 - May 9, 2027, 3 legs)", degrading to whatever is
// known. A trip with no name of its own is described rather than named.
function describeTrip(trip) {
  const parts = [trip?.dateRange, trip?.countLabel].filter(Boolean);
  const label = trip?.named ? `"${trip.name}"` : (trip?.name || 'this trip');
  return parts.length > 0 ? `${label} (${parts.join(', ')})` : label;
}

const CombineTripsModal = ({
  show,
  direction,
  thisTrip,
  otherTrip,
  losses,
  onHide,
  onCombine,
}) => {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // The dialog stays mounted between opens (only `show` flips), so each open
  // starts from the default rather than from the last edit. Keyed on `show`
  // alone on purpose: the page's own fetch can land while this is open, and
  // re-seeding then would overwrite what the user is typing.
  useEffect(() => {
    if (!show) return;
    // The other trip's name is the default -- "add this to that trip" is what
    // the button said. When that trip has no name of its own, this trip's name
    // is the only one anybody typed; when neither has one, the box starts
    // empty and the user has to name the trip they are creating.
    const seed = (otherTrip?.named && otherTrip.name)
      || (thisTrip?.named && thisTrip.name)
      || '';
    setName(seed);
    setError(null);
    setBusy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const trimmed = name.trim();

  async function submit(event) {
    event?.preventDefault();
    // A blank name would leave every record unnamed, and an unnamed record
    // stands alone as its own trip -- the merge would scatter both trips into
    // single legs. The button is disabled too; this covers Enter.
    if (trimmed === '' || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onCombine(trimmed);
      setBusy(false);
    } catch (err) {
      setError(extractErrorText(err));
      setBusy(false);
    }
  }

  function handleHide() {
    if (busy) return;
    setError(null);
    onHide();
  }

  const chips = [thisTrip, otherTrip]
    .filter((trip) => trip?.named && trip.name && trip.name !== name);

  return (
    <Modal show={show} onHide={handleHide}>
      <Modal.Header closeButton>
        <Modal.Title>Combine Trips</Modal.Title>
      </Modal.Header>
      <Form onSubmit={submit}>
        <Modal.Body>
          <p>
            Combine {describeTrip(thisTrip)} with the trip{' '}
            {direction === 'previous' ? 'before' : 'after'} it,{' '}
            {describeTrip(otherTrip)}.
          </p>

          <Form.Label htmlFor="combine-trip-name">Combined trip name</Form.Label>
          <Form.Control
            id="combine-trip-name"
            type="text"
            value={name}
            autoFocus
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
          />
          {chips.length > 0 && (
            <div className="mt-2 d-flex flex-wrap gap-2">
              {chips.map((trip) => (
                <Button
                  key={trip.key}
                  variant="outline-secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => setName(trip.name)}
                >
                  Use "{trip.name}"
                </Button>
              ))}
            </div>
          )}
          <Form.Text>
            Every leg of both trips takes this name. Their dates are not changed.
          </Form.Text>

          {losses && losses.length > 0 && (
            <Alert variant="warning" className="py-2 mb-0 mt-3">
              <ul className="mb-0 ps-3">
                {losses.map((loss) => <li key={loss}>{loss}</li>)}
              </ul>
            </Alert>
          )}

          {error && (
            <p className="text-danger small mb-0 mt-3" role="alert">{error}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleHide} disabled={busy}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy || trimmed === ''}>
            {busy ? 'Combining...' : 'Combine'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CombineTripsModal;
